import { sanitize } from '@vue-util/sanitize';
import type { UrlTemplate, Image } from '~~/shared/globalTypes/product';
import type { Promotions, GiftFormattedUpc, Promotion, GwpBadge, GiftProduct } from '../types';

export function getImageUrl(urlTemplate: UrlTemplate['product'] = '', filePath: Image['filePath'] = '') {
  const params = '?op_sharpen=1&wid=60&hei=78&fit=fit,1&$filtersmall$';

  return `${urlTemplate}${filePath}${params}`;
}

export function _normalizeUpc(upcs: Product['relationships']['upcs'], product: Product): GiftProduct['giftFormattedUpcs'] {
  const result: GiftProduct['giftFormattedUpcs'] = {};
  const colorMap = product?.traits?.colors?.colorMap || {};

  Object.values(upcs).forEach((upc) => {
    if (!upc?.availability?.available) return;
    result[upc?.id] = {
      upcId: upc?.id || 0,
      upcNumber: upc?.identifier?.upcNumber || '',
      name: colorMap[upc?.traits?.colors?.selectedColor]?.name || product?.detail?.name || '',
      images: colorMap[upc?.traits?.colors?.selectedColor]?.imagery?.images || [],
    };
  });

  return result;
}

export function _normalizeGiftProducts(giftProducts: GwpBadge['giftProducts']): Promotion['giftProductsById'] {
  const result: Promotion['giftProductsById'] = {};

  giftProducts.forEach((product) => {
    const normalizedUpcs = _normalizeUpc(product.relationships.upcs || {}, product);

    result[product.id] = {
      id: product.id,
      identifier: product?.identifier || {},
      detail: {
        name: sanitize(product.detail.name || ''),
      },
      urlTemplate: product.urlTemplate,
      giftFormattedUpcs: normalizedUpcs,
      isMultipleGiftUpcs: Object.keys(normalizedUpcs).length > 1,
      giftSelectedUpcId: 0, // Initially no UPC is selected
      alreadyInBag: false, // Initially not in bag
    };
  });

  return result;
}

export function normalizePromotions(badges: GwpBadge[]): Promotion[] {
  return badges.map((badge) => ({
    promoId: badge?.promoId || '',
    title: sanitize(badge?.checkoutDescription || ''),
    header: sanitize(badge?.header || ''),
    description: sanitize(badge?.description || ''),
    threshold: badge?.thresholdTriggers?.[0]?.threshold || 0,
    giftProductsById: _normalizeGiftProducts(badge?.giftProducts || []),
  }));
}

interface selectUpcResponce {
  upcId: number;
  alreadyInBag: boolean;
}

export function selectUpc(upcs: GiftFormattedUpc[], upcsInBag: Promotions['upcsInBag'], fallbackId: number): selectUpcResponce | null {
  if (!Array.isArray(upcs) || upcs.length === 0) return null;

  const matched = upcs.find((u) => upcsInBag.includes(u.upcId));
  if (matched) {
    return { upcId: matched.upcId, alreadyInBag: true };
  }

  if (fallbackId && upcs.some((u) => u.upcId === fallbackId)) {
    return { upcId: fallbackId, alreadyInBag: false };
  }

  const randomUpc = upcs[Math.floor(Math.random() * upcs.length)];
  return { upcId: randomUpc?.upcId || fallbackId, alreadyInBag: false };
}
